import { chartColor, chartColorPinned } from '@/shared/lib/chart-theme';
import {
  BarElement,
  CategoryScale,
  Chart,
  type ChartData,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  LinearScale,
  type Plugin,
  Title,
  Tooltip,
} from 'chart.js';
import { merge } from 'chart.js/helpers';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { type FC, type MouseEvent, type Ref } from 'react';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

export const basicHorizontalBarBackgroundPlugin = {
  id: 'horizontalBarBackground',
  beforeDatasetsDraw: (chart: Chart<'bar'>) => {
    const {
      ctx,
      scales: { x },
    } = chart;

    chart.data.datasets.forEach((_, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.type !== 'bar') return;

      meta.data.forEach((bar) => {
        const barElement = bar as BarElement;
        const { y: barElementY, height: barElementHeight } =
          barElement.getProps(['y', 'height'], true);
        if (barElementY == null || barElementHeight == null) return;

        const yPos = barElementY - barElementHeight / 2;
        const height = barElementHeight;
        const xZero = x.getPixelForValue(0);
        const xMax = x.getPixelForValue(x.max);

        ctx.save();
        ctx.fillStyle = chartColorPinned('--color-chart-track', '#d6dbed66');
        ctx.fillRect(xZero, yPos, xMax - xZero, height);
        ctx.restore();
      });
    });
  },
};

export const basicStackedHorizontalBarBackgroundPlugin = {
  id: 'horizontalBarBackground',
  beforeDatasetsDraw: (chart: Chart<'bar'>) => {
    const {
      ctx,
      scales: { x },
    } = chart;

    const xZero = x.getPixelForValue(0);
    const xMax = x.getPixelForValue(x.max);

    const drawnRows = new Map<number, { yPos: number; height: number }>();

    chart.data.datasets.forEach((_, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.type !== 'bar') return;

      meta.data.forEach((bar) => {
        const barElement = bar as BarElement;
        const { y: barElementY, height: barElementHeight } =
          barElement.getProps(['y', 'height'], true);
        if (barElementY == null || barElementHeight == null) return;

        const rowKey = Math.round(barElementY);
        if (!drawnRows.has(rowKey)) {
          drawnRows.set(rowKey, {
            yPos: barElementY - barElementHeight / 2,
            height: barElementHeight,
          });
        }
      });
    });

    drawnRows.forEach(({ yPos, height }) => {
      ctx.save();
      ctx.fillStyle = chartColorPinned('--color-chart-track', '#d6dbed66');
      ctx.fillRect(xZero, yPos, xMax - xZero, height);
      ctx.restore();
    });
  },
};

interface HorizontalBarChartProps {
  data: ChartData<'bar'>;
  options?: ChartOptions<'bar'>;
  plugins?: Plugin<'bar'>[];
  onLabelClick?: (index: number, label: string) => void;
  ref?: Ref<Chart<'bar'> | undefined>;
}

const HorizontalBarChart: FC<HorizontalBarChartProps> = ({
  data,
  options,
  plugins,
  onLabelClick,
  ref,
}) => {
  const defaultOptions: ChartOptions<'bar'> = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
      datalabels: {
        anchor: 'end',
        align: 'end',

        clip: true,
        color: chartColor('--color-neutral-8'),
        font: {
          size: 12,
        },

        formatter: (value: number) => value.toString(),
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        position: 'top',
        border: {
          display: false,
          dash: [2, 2],
          dashOffset: 1,
        },
        grid: {
          color: chartColor('--color-neutral-4'),
          tickBorderDash: [2, 5],
        },
      },
      y: {
        border: {
          display: false,
          dash: [2, 2],
          dashOffset: 1,
        },
        grid: {
          color: chartColor('--color-neutral-4'),
          tickBorderDash: [2, 5],
        },
      },
    },
    animation: {
      duration: 1200,
      easing: 'easeOutQuart',
    },
  };

  const mergedOptions = merge(defaultOptions, options ?? {});

  const handleClick = (event: MouseEvent<HTMLCanvasElement>) => {
    if (!onLabelClick) return;

    const chart = ChartJS.getChart(event.currentTarget);
    if (!chart) return;

    // Check if a bar element was clicked
    const elements = chart.getElementsAtEventForMode(
      event.nativeEvent,
      'nearest',
      { intersect: true },
      false
    );

    if (elements.length > 0) {
      const index = elements[0].index;
      const label = chart.data.labels?.[index];
      if (typeof label === 'string') {
        onLabelClick(index, label);
      }
      return;
    }

    // Check if a y-axis label was clicked
    const yScale = chart.scales['y'];
    if (!yScale) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Only handle clicks in the y-axis label area (left of the chart area)
    if (x > yScale.right) return;

    const labels = chart.data.labels ?? [];
    for (let i = 0; i < labels.length; i++) {
      const labelY = yScale.getPixelForTick(i);
      const halfStep = yScale.height / labels.length / 2;
      if (y >= labelY - halfStep && y <= labelY + halfStep) {
        const label = labels[i];
        if (typeof label === 'string') {
          onLabelClick(i, label);
        }
        return;
      }
    }
  };

  return (
    <Bar
      ref={ref}
      data={data}
      plugins={plugins}
      options={mergedOptions}
      style={onLabelClick ? { cursor: 'pointer' } : undefined}
      onClick={onLabelClick ? handleClick : undefined}
    />
  );
};

export { HorizontalBarChart };
